import { TagCategoryPicker } from '@/components/words/tag-category-picker';
import type { NoyauKey, TagCategory, WordTag } from '@/schemas/word';
import { useState } from 'react';

// Maps each noyauKey to its corresponding CSS color variable so tag chips
// can render the colored dot that links them to the PMS model.
export const NOYAU_CSS: Record<NoyauKey, string> = {
  bleunuit: 'var(--color-pms-bleu-nuit)',
  bleuciel: 'var(--color-pms-bleu-ciel)',
  vert: 'var(--color-pms-vert)',
  jaune: 'var(--color-pms-jaune)',
  rouge: 'var(--color-pms-rouge)',
  rose: 'var(--color-pms-rose)',
};

/** A category row key — every `TagCategory`, plus `'none'` for "Non catégorisé". */
export type CategoryRowKey = TagCategory | 'none';

export const CATEGORY_ROWS: { key: CategoryRowKey; label: string }[] = [
  { key: 'type', label: 'Type (langage)' },
  { key: 'date', label: 'Date' },
  { key: 'geo', label: 'Origines géographiques' },
  { key: 'metier', label: 'Métiers' },
  { key: 'langue', label: 'Langues' },
  { key: 'none', label: 'Non catégorisé' },
];

/** Group a word's tags by category row, defaulting an unset/unknown category to "Non catégorisé". */
export function groupTagsByCategory(tags: WordTag[]): Record<CategoryRowKey, WordTag[]> {
  const groups: Record<CategoryRowKey, WordTag[]> = {
    type: [],
    date: [],
    geo: [],
    metier: [],
    langue: [],
    none: [],
  };
  for (const tag of tags) {
    groups[tag.tagCategory ?? 'none'].push(tag);
  }
  return groups;
}

interface TagEditorProps {
  tags: WordTag[];
  readOnly?: boolean;
  onChange: (tags: WordTag[]) => void;
}

/**
 * Tag section for a word: one row per fixed category (+ "Non catégorisé"),
 * always rendered even when empty (no feature flag — see the rework decision
 * record, Point 1). Each row shows its tags as chips (removable in edit mode)
 * followed by a trailing `+` that opens {@link TagCategoryPicker} scoped to
 * that category.
 *
 * The component is purely controlled: it calls `onChange` with the full
 * updated tag array and leaves persistence to the parent (WordDetailEditor's
 * debounced PATCH).
 */
export function TagEditor({ tags, readOnly = false, onChange }: TagEditorProps) {
  const [openCategory, setOpenCategory] = useState<CategoryRowKey | null>(null);
  const [anchorPoint, setAnchorPoint] = useState<{ x: number; y: number } | undefined>(undefined);
  const groups = groupTagsByCategory(tags);

  function handleOpenPicker(key: CategoryRowKey, e: React.MouseEvent<HTMLButtonElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    setAnchorPoint({ x: rect.left, y: rect.bottom + 4 });
    setOpenCategory(key);
  }

  function handleRemove(label: string) {
    onChange(tags.filter((t) => t.label !== label));
  }

  return (
    <div className="words-tag-categories">
      {CATEGORY_ROWS.map(({ key, label }) => (
        <div key={key} className="words-tag-category">
          <span className="words-tag-category__label">{label}</span>
          <div className="words-tag-category__chips">
            {groups[key].map((tag) =>
              readOnly ? (
                <span key={tag.label} className="words-tag">
                  {tag.noyauKey && (
                    <span
                      className="words-tag__dot"
                      style={{ background: NOYAU_CSS[tag.noyauKey] }}
                    />
                  )}
                  {tag.label}
                </span>
              ) : (
                <RemovableTag key={tag.label} tag={tag} onRemove={handleRemove} />
              ),
            )}
            {!readOnly && (
              <button
                type="button"
                className="words-tag-category__add"
                aria-label={`Ajouter un tag « ${label} »`}
                onClick={(e) => handleOpenPicker(key, e)}
              >
                <span className="material-symbols-rounded" aria-hidden="true">
                  add
                </span>
              </button>
            )}
          </div>
        </div>
      ))}

      {openCategory !== null && (
        <TagCategoryPicker
          wordTags={tags}
          initialCategory={openCategory}
          anchorPoint={anchorPoint}
          onConfirm={(next) => {
            onChange(next);
            setOpenCategory(null);
          }}
          onClose={() => setOpenCategory(null)}
        />
      )}
    </div>
  );
}

// ── Removable chip ────────────────────────────────────────────────────────────

interface RemovableTagProps {
  tag: WordTag;
  onRemove: (label: string) => void;
}

/**
 * A single tag chip with an accessible remove button.
 *
 * The × button is hidden by default (opacity 0) and revealed to opacity 0.7
 * when the parent chip is hovered, then to full opacity on direct hover —
 * controlled entirely by CSS so no JS state is needed.
 *
 * If the tag carries a `noyauKey`, a small colored dot is shown to the left
 * of the label, associating the tag with its PMS noyau color.
 */
export function RemovableTag({ tag, onRemove }: RemovableTagProps) {
  return (
    <span className="words-tag words-tag--removable">
      {tag.noyauKey && (
        <span className="words-tag__dot" style={{ background: NOYAU_CSS[tag.noyauKey] }} />
      )}
      {tag.label}
      <button
        type="button"
        className="words-tag__remove"
        onClick={() => onRemove(tag.label)}
        aria-label={`Supprimer le tag « ${tag.label} »`}
      >
        <span className="material-symbols-rounded" aria-hidden="true">
          close
        </span>
      </button>
    </span>
  );
}
